import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider, createRouter } from "@tanstack/react-router"
import { StrictMode } from "react"
import ReactDOM from "react-dom/client"

// Import the generated route tree
import { routeTree } from "./routeTree.gen"
import type { RouterContext } from "./lib/routerContext"

import "./styles/index.css"

// Import i18n configuration
import { GoogleMapsProvider } from "./lib/GoogleMapsProvider"
import "./lib/i18n"
import LanguageProvider from "./lib/LanguageProvider"
import { PassphraseProvider } from "./lib/PassphraseProvider"
import { ThemeProvider } from "./lib/ThemeProvider"
import { loadReactScan } from "./lib/loadReactScan"

if (import.meta.env.VITE_C2_VERSION === "True") {
  console.log("C2 Version")
  localStorage.removeItem("custom-theme-json")
}

// Create a new router instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      retry: 2,
    },
  },
})

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  } satisfies RouterContext,
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById("app")

async function renderApp() {
  if (!rootElement || rootElement.innerHTML) {
    return
  }

  await loadReactScan()

  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <PassphraseProvider>
          <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
            <LanguageProvider>
              <GoogleMapsProvider>
                <RouterProvider router={router} />
              </GoogleMapsProvider>
            </LanguageProvider>
          </ThemeProvider>
        </PassphraseProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}

void renderApp()
